import { alpha, Box, ButtonBase, styled } from "@mui/material";
import { Paragraph, Span } from "components/Typography";
import { useLocation, useNavigate } from "react-router-dom";
import {
  navigations,
  navigations_participant,
} from "../layout-parts/navigation-original";
//import { navigations } from "../layout-parts/navigation";// use this to view all template pages....
import { navigations_instructor } from "../layout-parts/navigation-original";
import { ADMIN_ROLE_ID, INSTRUCTOR_ROLE_ID } from "../../constants";
import DashboardIcon from "@mui/icons-material/Dashboard";

import SidebarAccordion from "./SidebarAccordion";
import useAuth from "hooks/useAuth";
import { useTranslation } from "react-i18next";

const NavItemButton = styled(ButtonBase)(({ theme, active }) => ({
  height: 44,
  width: "100%",
  borderRadius: 8,
  marginBottom: 4,
  padding: "0 18px",
  justifyContent: "flex-start",
  transition: "all 0.15s ease",
  ...(active && {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  }),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));
const ListLabel = styled(Paragraph)(({ theme, compact }) => ({
  fontWeight: 700,
  fontSize: "12px",
  marginTop: "20px",
  marginLeft: "15px",
  marginBottom: "10px",
  textTransform: "uppercase",
  transition: "all 0.15s ease",
  color: theme.palette.text.secondary,
  ...(compact && {
    opacity: 0,
    width: 0,
  }),
}));
const ExternalLink = styled("a")(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "pre",
  marginBottom: "8px",
  textDecoration: "none",
  color: theme.palette.text.primary,
}));
const StyledText = styled(Span)(({ theme, compact, active }) => ({
  whiteSpace: "nowrap",
  paddingLeft: "0.8rem",
  transition: "all 0.15s ease",
  fontSize: "13px",
  fontWeight: 500,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  ...(compact && {
    opacity: 0,
    width: 0,
  }),
}));
const BulletIcon = styled("div")(({ theme, active }) => ({
  width: 4,
  height: 4,
  marginLeft: "10px",
  marginRight: "8px",
  overflow: "hidden",
  borderRadius: "50%",
  background: active ? theme.palette.primary.main : theme.palette.text.disabled,
  boxShadow: active ? `0px 0px 0px 3px ${theme.palette.primary[200]}` : "none",
}));
const BadgeValue = styled(Box)(({ compact, theme }) => ({
  color: "white",
  fontSize: "12px",
  fontWeight: 500,
  padding: "1px 6px",
  overflow: "hidden",
  borderRadius: "300px",
  backgroundColor: theme.palette.primary.main,
  display: compact ? "none" : "unset",
})); // Common icon style

const iconStyle = (active) => ({
  fontSize: 18,
  marginRight: "4px",
  color: active ? "primary.main" : "text.secondary",
});

const MultiLevelMenu = (props) => {
  const { sidebarCompact, participantMenu = false } = props;
  const navigate = useNavigate();
  const { role } = useAuth();
  const { t } = useTranslation();

  const { pathname } = useLocation(); // handle active current page

  const activeRoute = (path) => (pathname === path ? 1 : 0); // handle navigate to another route or page

  const handleNavigation = (path) => navigate(path); //   RECURSIVE FUNCTION TO RENDER MULTI LEVEL MENU

  const renderMenu = (data) => {
    const filteredData = data.filter((item) => {
      // Filter out items that do not have the required permission
      if (item.type === "label") return true; // Always show labels
      if (item.type === "extLink") return true; // Always show external links
      if (item.children && item.children.length > 0) {
        // If it has children, check if at least one child has the permission
        return item.children.some(
          (child) =>
            !!role?.permissions?.find((p) =>
              p?.permission?.routeJson?.includes(child.path)
            )
        );
      }
      // Otherwise, check if the item itself has the permission
      return !!role?.permissions?.find((p) =>
        p?.permission?.routeJson?.includes(item.path)
      );
    });

    return filteredData.map((item, index) => {
      if (item.type === "label" && !!filteredData[index + 1])
        return (
          <ListLabel key={index} compact={sidebarCompact}>
            {item.label}
          </ListLabel>
        );

      if (item.children && item.children.length > 0) {
        // If at least one child is present and has the permission, render the accordion
        return (
          <SidebarAccordion
            key={index}
            item={item}
            sidebarCompact={sidebarCompact}
          >
            {renderMenu(item.children)}
          </SidebarAccordion>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            rel="noopener noreferrer"
            target="_blank"
          >
            <NavItemButton key={item.name} name="child" active={0}>
              {(() => {
                if (item.icon) {
                  return <item.icon sx={iconStyle(0)} />;
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}

              <StyledText
                compact={sidebarCompact}
                active={activeRoute(item.path)}
              >
                {item.name}
              </StyledText>

              <Box mx="auto" />

              {item.badge && (
                <BadgeValue compact={sidebarCompact}>
                  {item.badge.value}
                </BadgeValue>
              )}
            </NavItemButton>
          </ExternalLink>
        );
      } else {
        if (
          !!role?.permissions?.find((p) =>
            p?.permission?.routeJson?.includes(item.path)
          )
        ) {
          return (
            <Box key={index}>
              <NavItemButton
                key={item.name}
                className="navItem"
                active={activeRoute(item.path)}
                onClick={() => handleNavigation(item.path)}
              >
                {item?.icon ? (
                  <item.icon sx={iconStyle(activeRoute(item.path))} />
                ) : (
                  <BulletIcon active={activeRoute(item.path)} />
                )}

                <StyledText
                  compact={sidebarCompact}
                  active={activeRoute(item.path)}
                >
                  {item.name}
                </StyledText>

                <Box mx="auto" />

                {item.badge && (
                  <BadgeValue compact={sidebarCompact}>
                    {item.badge.value}
                  </BadgeValue>
                )}
              </NavItemButton>
            </Box>
          );
        } else {
          return null;
        }
      }
    });
  };

  const renderParticipantMenu = (data) => {
    return data.map((item, index) => {
      if (item.type === "label")
        return (
          <ListLabel key={index} compact={sidebarCompact}>
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <SidebarAccordion
            key={index}
            item={item}
            sidebarCompact={sidebarCompact}
          >
            {renderParticipantMenu(item.children)}
          </SidebarAccordion>
        );
      } else if (item.type === "extLink") {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            rel="noopener noreferrer"
            target="_blank"
          >
            <NavItemButton key={item.name} name="child" active={0}>
              {(() => {
                if (item.icon) {
                  return <item.icon sx={iconStyle(0)} />;
                } else {
                  return (
                    <span className="item-icon icon-text">{item.iconText}</span>
                  );
                }
              })()}

              <StyledText
                compact={sidebarCompact}
                active={activeRoute(item.path)}
              >
                {item.name}
              </StyledText>

              <Box mx="auto" />

              {item.badge && (
                <BadgeValue compact={sidebarCompact}>
                  {item.badge.value}
                </BadgeValue>
              )}
            </NavItemButton>
          </ExternalLink>
        );
      } else {
        return (
          <Box key={index}>
            <NavItemButton
              key={item.name}
              className="navItem"
              active={activeRoute(item.path)}
              onClick={() => handleNavigation(item.path)}
            >
              {item?.icon ? (
                <item.icon sx={iconStyle(activeRoute(item.path))} />
              ) : (
                <BulletIcon active={activeRoute(item.path)} />
              )}

              <StyledText
                compact={sidebarCompact}
                active={activeRoute(item.path)}
              >
                {item.name}
              </StyledText>

              <Box mx="auto" />

              {item.badge && (
                <BadgeValue compact={sidebarCompact}>
                  {item.badge.value}
                </BadgeValue>
              )}
            </NavItemButton>
          </Box>
        );
      }
    });
  };

  if (participantMenu) {
    return <>{renderParticipantMenu(navigations_participant)}</>;
  }

  return (
    <>
      {renderMenu(
        role?.id == INSTRUCTOR_ROLE_ID ? navigations_instructor : navigations
      )}
    </>
  );
};

export default MultiLevelMenu;
